import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config.js';
import { useAuth } from '../context/AuthContext.js';
import { useToast } from '../context/ToastContext.js';
import './PracticePlanModal.css';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000,
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '24px'
};
const sheet = {
  background: '#fff', borderRadius: 12, width: 'min(1000px, 96vw)', margin: 'auto',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
};

// Physical stations (columns of the rotation table) — the equipment that can conflict
const STATIONS = [
  { key: 'vault', label: 'Vault', emoji: '🐎' },
  { key: 'bars', label: 'Bars', emoji: '➰' },
  { key: 'beam', label: 'Beam', emoji: '🎋' },
  { key: 'floor', label: 'Floor', emoji: '🤸' },
  { key: 'trampoline', label: 'Tramp', emoji: '⛹️' },
  { key: 'tumble', label: 'Tumble', emoji: '🔁' },
];
const WHOLE_PRESETS = ['Stretch', 'Warm-up', 'Conditioning', 'Choreography', 'Strength'];
const STATION_CAP = 9; // soft overload threshold — kids on one station in a block
const RT_COLORS = ['#3b82f6', '#ef4444', '#22c55e', '#eab308', '#a855f7', '#ec4899', '#14b8a6', '#f97316'];
const normStation = (a) => String(a || '').trim().toLowerCase();
// HH:MM string overlap
const overlaps = (aS, aE, bS, bE) => !!(aS && aE && bS && bE) && aS < bE && bS < aE;
// JSON scope columns come back parsed by mysql2, but tolerate a raw string too.
const parseScope = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string') { try { const p = JSON.parse(v); return Array.isArray(p) ? p : null; } catch { return null; } }
  return null;
};


function PracticePlanModal({ classId, calendarEventId, practiceDate, className, athletes = [], onClose }) {
  const { user } = useAuth();
  const toast = useToast();
  const token = user?.token;
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [plan, setPlan] = useState(null);
  const [coaches, setCoaches] = useState([]);
  const [tab, setTab] = useState('board');
  const [loading, setLoading] = useState(true);
  const [drills, setDrills] = useState([]);
  const [gymFloor, setGymFloor] = useState([]); // cross-class apparatus occupancy for the date
  const [recentPlans, setRecentPlans] = useState([]); // prior practices for "copy from"
  const [prep, setPrep] = useState(null);              // data-fed authoring: ease-up / needs-work
  const [prepLoading, setPrepLoading] = useState(false);
  const [armedGroup, setArmedGroup] = useState(null); // tap-to-place: the group being placed
  const [showSeed, setShowSeed] = useState(false);
  const [seedCfg, setSeedCfg] = useState({ warmup: 15, buffer: 3, rotations: 'auto' });
  const [showQuick, setShowQuick] = useState(false);
  const [quickCfg, setQuickCfg] = useState({ count: '', by_level: false });
  const [quickBusy, setQuickBusy] = useState(false);
  const [showMoreRecent, setShowMoreRecent] = useState(false);
  // Session Plan drills, redesigned as grouped sections (heading + Name/Count/Unit rows)
  const [newSectionName, setNewSectionName] = useState('');
  const [rowDrafts, setRowDrafts] = useState({});      // section -> { name, count, unit }
  const [draftSections, setDraftSections] = useState([]); // headings added but not yet saved
  // push-to-coach assignment form
  const [assignForm, setAssignForm] = useState({ coach_id: '', apparatus: [], groups: [], note: '' });

  // layer form
  const [layers, setLayers] = useState({ theme: '', goals: '', workout_plan: '', conditioning_plan: '' });
  // apply-session-to-more-days form
  const [showApply, setShowApply] = useState(false);
  const [applyForm, setApplyForm] = useState({
    weekdays: [new Date(`${practiceDate}T00:00:00`).getDay()],
    weeks: 4,
    include: { theme: true, goals: true, drills: true, workout: true, conditioning: true }
  });

  const applyPlan = (p) => {
    setPlan(p);
    if (p) setLayers({
      theme: p.theme || '', goals: p.goals || '',
      workout_plan: p.workout_plan || '', conditioning_plan: p.conditioning_plan || ''
    });
  };

  const loadPlan = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/v1/practice-plans`, {
        class_id: classId || undefined,
        calendar_event_id: calendarEventId || undefined,
        practice_date: practiceDate
      }, auth);
      applyPlan(res.data.plan);
    } catch (err) {
      toast?.error?.('Failed to load practice plan');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, calendarEventId, practiceDate]);

  useEffect(() => {
    loadPlan();
    axios.get(`${API_BASE_URL}/api/admin/users?role=coach,admin&active_only=true`, auth)
      .then(r => setCoaches(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
    axios.get(`${API_BASE_URL}/api/v1/rotations/drills`, auth)
      .then(r => setDrills(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
    axios.get(`${API_BASE_URL}/api/v1/practice-plans/gym-floor?date=${practiceDate}`, auth)
      .then(r => setGymFloor(r.data?.occupancy || []))
      .catch(() => {});
    if (classId) {
      axios.get(`${API_BASE_URL}/api/v1/practice-plans/recent?class_id=${classId}&before=${practiceDate}`, auth)
        .then(r => setRecentPlans(Array.isArray(r.data) ? r.data : []))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadPlan]);

  // Prep signals are class-scoped and only needed once the coach opens the tab.
  useEffect(() => {
    if (tab !== 'prep' || !classId || prep) return;
    setPrepLoading(true);
    axios.get(`${API_BASE_URL}/api/v1/practice-plans/prep?class_id=${classId}&date=${practiceDate}`, auth)
      .then(r => setPrep(r.data || { ease_up: [], needs_work: [] }))
      .catch(() => setPrep({ ease_up: [], needs_work: [] }))
      .finally(() => setPrepLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, classId, practiceDate]);

  const planUrl = (suffix = '') => `${API_BASE_URL}/api/v1/practice-plans/${plan.id}${suffix}`;
  const mutate = async (fn) => {
    try {
      const res = await fn();
      if (res?.data?.plan) applyPlan(res.data.plan);
    } catch (err) {
      toast?.error?.(err.response?.data?.error || 'Action failed');
    }
  };

  // ---- rotations (one activity per rotation) ----
  const addSlot = () => {
    const n = plan.slots?.length || 0;
    mutate(() => axios.post(planUrl('/slots'), { label: `R${n + 1}`, slot_order: n }, auth));
  };
  const updateSlot = (slotId, patch) => mutate(() => axios.put(planUrl(`/slots/${slotId}`), patch, auth));
  const deleteSlot = (slotId) => mutate(() => axios.delete(planUrl(`/slots/${slotId}`), auth));

  // ---- groups (with head-counts) ----
  const addGroup = () => {
    const n = plan.groups?.length || 0;
    mutate(() => axios.post(planUrl('/groups'), { group_label: `Group ${String.fromCharCode(65 + (n % 26))}`, group_order: n }, auth));
  };
  const updateGroup = (id, patch) => mutate(() => axios.put(planUrl(`/groups/${id}`), patch, auth));
  const deleteGroup = (id) => mutate(() => axios.delete(planUrl(`/groups/${id}`), auth));

  // ---- placements (which group is at which station this block) ----
  const setPlacement = (slotId, groupId, stationKey) =>
    mutate(() => axios.put(planUrl('/group-placements'), { slot_id: slotId, group_id: Number(groupId), apparatus: stationKey }, auth));
  const removePlacement = (slotId, groupId) =>
    mutate(() => axios.delete(planUrl(`/group-placements/${slotId}/${groupId}`), auth));
  // Update just the coach or note on an already-placed cell (apparatus untouched).
  const setPlacementMeta = (slotId, groupId, patch) =>
    mutate(() => axios.put(planUrl('/group-placements'), { slot_id: slotId, group_id: Number(groupId), ...patch }, auth));

  // ---- table helpers ----
  const groupIndex = (id) => (plan?.groups || []).findIndex(g => g.id === id);
  const groupColor = (id) => RT_COLORS[Math.max(0, groupIndex(id)) % RT_COLORS.length];
  // Each placed cell carries its group, plus the placement row (coach_id, note).
  const placementsInCell = (slotId, stationKey) =>
    (plan?.group_placements || [])
      .filter(gp => gp.slot_id === slotId && normStation(gp.apparatus) === stationKey)
      .map(gp => ({ gp, group: (plan.groups || []).find(g => g.id === gp.group_id) }))
      .filter(x => x.group);
  const groupsInCell = (slotId, stationKey) => placementsInCell(slotId, stationKey).map(x => x.group);
  const heldByOther = (slot, stationKey) => {
    const sS = (slot.start_time || '').slice(0, 5), sE = (slot.end_time || '').slice(0, 5);
    const hit = gymFloor.find(o => o.plan_id !== plan.id && normStation(o.apparatus) === stationKey && overlaps(sS, sE, o.start_time, o.end_time));
    return hit ? (hit.class_name || 'another class') : null;
  };
  const cellKids = (slotId, stationKey) =>
    groupsInCell(slotId, stationKey).reduce((s, g) => s + (Number(g.headcount) || 0), 0);
  const totalKids = (plan?.groups || []).reduce((s, g) => s + (Number(g.headcount) || 0), 0);

  // ---- one-tap builders ----
  const buildRotations = () => {
    const force = (plan?.slots || []).length > 0;
    if (force && !window.confirm('Rebuild time blocks? This replaces the current blocks & group placements.')) return;
    mutate(() => axios.post(planUrl('/seed-rotations'),
      { warmup: seedCfg.warmup, buffer: seedCfg.buffer, rotations: seedCfg.rotations, force }, auth));
    setShowSeed(false);
  };
  const autoRotateFill = () => mutate(() => axios.post(planUrl('/auto-rotate'), {}, auth));

  // ⚡ Quick-Build: empty board → full starter practice in one action.
  // Splits the class roster into real athlete-groups, lays out timed rotations
  // from the schedule window, then auto-rotates them across apparatus. The coach
  // edits from there — no more hand-building every practice from scratch.
  const quickBuild = async () => {
    const hasBoard = (plan?.groups?.length || plan?.slots?.length);
    if (hasBoard && !window.confirm('Build a fresh practice from the roster? This replaces the current groups & rotation board.')) return;
    setQuickBusy(true);
    try {
      await axios.post(planUrl('/roster-groups'),
        { count: quickCfg.count ? parseInt(quickCfg.count) : undefined, by_level: quickCfg.by_level }, auth);
      await axios.post(planUrl('/seed-rotations'),
        { warmup: seedCfg.warmup, buffer: seedCfg.buffer, rotations: seedCfg.rotations, force: true }, auth);
      const res = await axios.post(planUrl('/auto-rotate'), {}, auth);
      if (res?.data?.plan) applyPlan(res.data.plan);
      setShowQuick(false);
      toast?.success?.('Practice built from your roster — tweak anything you like! ⚡');
    } catch (err) {
      toast?.error?.(err.response?.data?.error || 'Quick-build failed');
    } finally {
      setQuickBusy(false);
    }
  };
  const copyFrom = async (sourceId, label) => {
    if (!sourceId) return;
    setQuickBusy(true);
    try {
      const res = await axios.post(planUrl('/copy-from'), { source_plan_id: sourceId }, auth);
      if (res?.data?.plan) applyPlan(res.data.plan);
      toast?.success?.(label ? `Copied ${label} — tweak anything you like!` : 'Practice copied.');
    } catch (err) {
      toast?.error?.(err.response?.data?.error || 'Copy failed');
    } finally {
      setQuickBusy(false);
    }
  };
  const prettyDate = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  // tap-to-place: tap a droppable cell → place the armed group there (moves if already placed this block)
  const tapCell = (slotId, stationKey) => { if (armedGroup) setPlacement(slotId, armedGroup, stationKey); };

  // ---- layers ----
  const autoSaveLayers = () => { if (plan) mutate(() => axios.put(planUrl('/layers'), layers, auth)); };
  const saveLayers = async () => {
    if (!plan) return;
    try {
      const res = await axios.put(planUrl('/layers'), layers, auth);
      if (res?.data?.plan) applyPlan(res.data.plan);
      toast?.success?.('Session saved');
    } catch (err) {
      toast?.error?.(err.response?.data?.error || 'Failed to save session');
    }
  };

  // ---- activities (drills) ----
  const deleteActivity = (activityId) => mutate(() => axios.delete(planUrl(`/activities/${activityId}`), auth));

  // ---- sectioned drill lists (Name · Count · Unit, grouped by heading) ----
  const UNGROUPED = 'Ungrouped';
  const sectionKey = (a) => a.section || UNGROUPED;
  const sectionActivities = (sec) =>
    (plan?.activities || []).filter(a => sectionKey(a) === sec)
      .sort((x, y) => (x.activity_order || 0) - (y.activity_order || 0));
  const orderedSections = () => {
    const order = [];
    (plan?.activities || []).forEach(a => { const s = sectionKey(a); if (!order.includes(s)) order.push(s); });
    draftSections.forEach(s => { if (!order.includes(s)) order.push(s); });
    return order;
  };
  const setDraft = (sec, patch) =>
    setRowDrafts(d => ({ ...d, [sec]: { name: '', count: '', unit: 'turns', ...(d[sec] || {}), ...patch } }));
  const addSectionHeading = () => {
    const name = newSectionName.trim();
    if (!name) return;
    if (orderedSections().includes(name)) { toast?.error?.('That section already exists'); return; }
    setDraftSections(s => [...s, name]);
    setNewSectionName('');
  };
  const addRow = (sec) => {
    const draft = rowDrafts[sec] || {};
    const name = (draft.name || '').trim();
    if (!name) { toast?.error?.('Name the skill / drill first'); return; }
    mutate(() => axios.post(planUrl('/activities'), {
      activity_name: name,
      section: sec === UNGROUPED ? null : sec,
      reps: draft.count === '' || draft.count == null ? null : parseInt(draft.count),
      count_unit: draft.unit || 'turns',
      activity_order: (plan.activities?.length || 0)
    }, auth));
    setRowDrafts(d => ({ ...d, [sec]: { name: '', count: '', unit: draft.unit || 'turns' } }));
    setDraftSections(s => s.filter(x => x !== sec)); // now backed by a real row
  };
  const updateRow = (id, patch) => mutate(() => axios.put(planUrl(`/activities/${id}`), patch, auth));
  const renameSection = (from, to) => {
    const t = (to || '').trim();
    if (!t || t === from) return;
    if (from !== UNGROUPED && !draftSections.includes(from) &&
        !(plan.activities || []).some(a => sectionKey(a) === from)) return;
    setDraftSections(s => s.map(x => (x === from ? t : x)));
    if ((plan.activities || []).some(a => sectionKey(a) === from)) {
      mutate(() => axios.put(planUrl('/activity-sections/rename'), { from: from === UNGROUPED ? null : from, to: t }, auth));
    }
  };
  const deleteSection = (sec) => {
    const hasRows = (plan.activities || []).some(a => sectionKey(a) === sec);
    if (!hasRows) { setDraftSections(s => s.filter(x => x !== sec)); return; }
    if (!window.confirm(`Delete the "${sec}" section and all its items?`)) return;
    mutate(() => axios.delete(planUrl(`/activity-sections/${encodeURIComponent(sec)}`), auth));
  };
  const UNITS = ['turns', 'reps', 'sets', 'total', 'min', 'sec'];

  // ---- push a scoped slice of this plan to a coach ----
  const toggleAssignApp = (k) => setAssignForm(f => ({ ...f, apparatus: f.apparatus.includes(k) ? f.apparatus.filter(x => x !== k) : [...f.apparatus, k] }));
  const toggleAssignGroup = (id) => setAssignForm(f => ({ ...f, groups: f.groups.includes(id) ? f.groups.filter(x => x !== id) : [...f.groups, id] }));
  const pushAssignment = () => {
    if (!assignForm.coach_id) { toast?.error?.('Pick a coach to push to'); return; }
    mutate(() => axios.post(planUrl('/assignments'), {
      coach_id: Number(assignForm.coach_id),
      apparatus_scope: assignForm.apparatus,
      group_scope: assignForm.groups,
      note: assignForm.note || null
    }, auth));
    setAssignForm({ coach_id: '', apparatus: [], groups: [], note: '' });
    toast?.success?.('Pushed to coach');
  };
  const removeAssignment = (id) => mutate(() => axios.delete(planUrl(`/assignments/${id}`), auth));

  // Coaches already placed on the board (block coach = whole practice; cell coach =
  // that apparatus + group). This is what the Assign tab derives from — no re-picking.
  const boardCoaches = useMemo(() => {
    const map = {};
    (plan?.group_placements || []).forEach(gp => {
      if (!gp.coach_id) return;
      const e = (map[gp.coach_id] = map[gp.coach_id] || { id: gp.coach_id, name: gp.coach_name, apparatus: new Set(), groups: new Set(), all: false });
      if (gp.apparatus) e.apparatus.add(normStation(gp.apparatus));
      if (gp.group_id) e.groups.add(gp.group_id);
    });
    (plan?.slots || []).forEach(s => {
      if (!s.coach_id) return;
      const e = (map[s.coach_id] = map[s.coach_id] || { id: s.coach_id, name: s.coach_name, apparatus: new Set(), groups: new Set(), all: false });
      e.all = true;
    });
    return Object.values(map);
  }, [plan]);

  const notifyCoaches = async () => {
    try {
      const res = await axios.post(planUrl('/notify-coaches'), {}, auth);
      if (res?.data?.plan) applyPlan(res.data.plan);
      toast?.success?.(`Notified ${res.data.notified} coach${res.data.notified === 1 ? '' : 'es'} — they'll see their part on Today.`);
    } catch (err) {
      toast?.error?.(err.response?.data?.error || 'Failed to notify coaches');
    }
  };
  const [showManualAssign, setShowManualAssign] = useState(false);

  // ---- apply session content to more days ----
  const toggleApplyWeekday = (wd) => setApplyForm(f => ({
    ...f, weekdays: f.weekdays.includes(wd) ? f.weekdays.filter(x => x !== wd) : [...f.weekdays, wd]
  }));
  const toggleInclude = (key) => setApplyForm(f => ({ ...f, include: { ...f.include, [key]: !f.include[key] } }));
  const runApply = async () => {
    if (!applyForm.weekdays.length) { toast?.error?.('Pick at least one day of the week'); return; }
    try {
      const res = await axios.post(planUrl('/apply-to'), {
        weekdays: applyForm.weekdays,
        start_date: practiceDate,
        weeks: applyForm.weeks,
        include: applyForm.include
      }, auth);
      toast?.success?.(`Applied this session to ${res.data.applied} day${res.data.applied === 1 ? '' : 's'}`);
      setShowApply(false);
    } catch (err) {
      toast?.error?.(err.response?.data?.error || 'Apply failed');
    }
  };

  const tabBtn = (id, label) => (
    <button
      onClick={() => setTab(id)}
      style={{
        padding: '8px 14px', border: 'none', cursor: 'pointer', fontWeight: 600,
        borderBottom: tab === id ? '3px solid #4f46e5' : '3px solid transparent',
        background: 'transparent', color: tab === id ? '#4f46e5' : '#555'
      }}
    >{label}</button>
  );

  return (
    <div style={overlay} onClick={onClose}>
      <div style={sheet} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #eee' }}>
          <div>
            <h2 style={{ margin: 0 }}>🗓️ Practice Plan</h2>
            <small style={{ color: '#666' }}>
              {className ? `${className} · ` : ''}{new Date(`${practiceDate}T00:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
            </small>
          </div>
          <button onClick={onClose} style={{ fontSize: 22, border: 'none', background: 'transparent', cursor: 'pointer' }}>×</button>
        </div>

        <div style={{ display: 'flex', gap: 4, padding: '0 12px', borderBottom: '1px solid #eee', flexWrap: 'wrap' }}>
          {classId && tabBtn('prep', '🧭 Prep')}
          {tabBtn('board', '🔄 Rotation Board')}
          {tabBtn('session', '📋 Session Plan')}
          {tabBtn('assign', '📤 Assign')}
        </div>

        <div style={{ padding: 20, minHeight: 320 }}>
          {loading || !plan ? (
            <p>Loading…</p>
          ) : (
            <>
              {/* ROTATION TABLE — time blocks × stations, gym-floor aware */}
              {tab === 'board' && (
                <div className="rt-board">
                  <div className="rot-focus">
                    <label className="rot-focus-field">🎯 Theme
                      <input value={layers.theme} onChange={e => setLayers({ ...layers, theme: e.target.value })}
                        onBlur={autoSaveLayers} placeholder="Focus of the day" />
                    </label>
                    <label className="rot-focus-field">✅ Goals
                      <input value={layers.goals} onChange={e => setLayers({ ...layers, goals: e.target.value })}
                        onBlur={autoSaveLayers} placeholder="What should they accomplish?" />
                    </label>
                  </div>

                  {/* Empty-board front door → repeat last practice (one tap) or build from roster */}
                  {classId && (plan.groups || []).length === 0 && (plan.slots || []).length === 0 && (
                    <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 12, padding: '14px 16px', margin: '4px 0 12px' }}>
                      {recentPlans.length > 0 ? (
                        <>
                          <div style={{ fontSize: '.82rem', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: '#7c3aed', marginBottom: 8 }}>
                            Start this practice
                          </div>
                          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                            <button onClick={() => copyFrom(recentPlans[0].id, prettyDate(recentPlans[0].practice_date) + "'s practice")} disabled={quickBusy}
                              style={{ background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 16px', fontWeight: 700, cursor: 'pointer', textAlign: 'left', lineHeight: 1.3 }}>
                              <div style={{ fontSize: '.98rem' }}>{quickBusy ? '⏳ Copying…' : '🔁 Repeat last practice'}</div>
                              <div style={{ fontSize: '.78rem', fontWeight: 500, opacity: .9, marginTop: 2 }}>
                                {prettyDate(recentPlans[0].practice_date)}
                                {recentPlans[0].theme ? ` · ${recentPlans[0].theme}` : ''}
                                {` · ${recentPlans[0].slot_count} block${recentPlans[0].slot_count === 1 ? '' : 's'}`}
                                {recentPlans[0].group_count > 0 ? ` · ${recentPlans[0].group_count} groups` : ''}
                              </div>
                            </button>
                            <span style={{ fontSize: '.85rem', color: '#6b7280' }}>copies groups, blocks, coaches & focus — edit after</span>
                            {recentPlans.length > 1 && (
                              <button onClick={() => setShowMoreRecent(v => !v)}
                                style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#4f46e5', cursor: 'pointer', fontSize: '.85rem', fontWeight: 600 }}>
                                {showMoreRecent ? '▲ Hide' : '📋 Another day…'}
                              </button>
                            )}
                          </div>
                          {showMoreRecent && recentPlans.length > 1 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
                              {recentPlans.slice(1).map(p => (
                                <button key={p.id} onClick={() => copyFrom(p.id, prettyDate(p.practice_date) + "'s practice")} disabled={quickBusy}
                                  style={{ textAlign: 'left', background: '#fff', border: '1px solid #e9d5ff', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontSize: '.85rem', color: '#4c1d95' }}>
                                  <strong>{prettyDate(p.practice_date)}</strong>
                                  {p.theme ? ` · ${p.theme}` : ''}{` · ${p.slot_count} blocks`}
                                </button>
                              ))}
                            </div>
                          )}
                          <div style={{ borderTop: '1px solid #ede9fe', margin: '12px 0 10px' }} />
                          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '.85rem', color: '#6b7280' }}>Fresh instead?</span>
                            <button onClick={() => setShowQuick(true)} disabled={quickBusy}
                              style={{ background: '#fff', color: '#7c3aed', border: '1px solid #c4b5fd', borderRadius: 8, padding: '7px 14px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              ⚡ Quick-Build from roster
                            </button>
                          </div>
                        </>
                      ) : (
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '.9rem', color: '#5b21b6' }}>
                            <strong>Start fast.</strong> Build a full practice from your class roster in one tap — groups, timed rotations, and apparatus rotate. Edit after.
                          </span>
                          <button onClick={() => setShowQuick(true)} disabled={quickBusy}
                            style={{ marginLeft: 'auto', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            ⚡ Quick-Build
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Groups + head-counts */}
                  <div className="rt-groups">
                    <span className="rt-groups-label">Groups</span>
                    {(plan.groups || []).map((g) => (
                      <span key={g.id} className={`rt-group-chip ${armedGroup === g.id ? 'armed' : ''}`} style={{ borderColor: groupColor(g.id) }}>
                        <button className="rt-group-dot" style={{ background: groupColor(g.id) }}
                          title="Tap to place this group in cells" onClick={() => setArmedGroup(armedGroup === g.id ? null : g.id)} />
                        <input className="rt-group-name" defaultValue={g.group_label}
                          onBlur={e => e.target.value.trim() && e.target.value !== g.group_label && updateGroup(g.id, { group_label: e.target.value.trim() })} />
                        <input className="rt-group-num" type="number" min="0" placeholder="#" defaultValue={g.headcount || ''}
                          onBlur={e => updateGroup(g.id, { headcount: e.target.value })} />
                        <button className="rt-group-x" onClick={() => deleteGroup(g.id)}>✕</button>
                      </span>
                    ))}
                    <button className="rt-add-group" onClick={addGroup}>＋ Group</button>
                    {totalKids > 0 && <span className="rt-total-kids">👥 {totalKids} kids</span>}
                  </div>

                  {/* One-tap builders */}
                  <div className="rt-toolbar">
                    {classId && (
                      <button className="rt-tool primary" onClick={() => { setShowQuick(v => !v); setShowSeed(false); }} disabled={quickBusy}
                        style={{ background: '#7c3aed', color: '#fff', borderColor: '#7c3aed' }}>
                        {quickBusy ? '⏳ Building…' : '⚡ Quick-Build'}
                      </button>
                    )}
                    <button className="rt-tool primary" onClick={() => { setShowSeed(v => !v); setShowQuick(false); }}>🪄 Auto-build times</button>
                    {(plan.groups || []).length > 0 && (plan.slots || []).some(s => !s.activity_label) && (
                      <button className="rt-tool" onClick={autoRotateFill}>🔄 Auto-rotate</button>
                    )}
                    {recentPlans.length > 0 && (
                      <select className="rt-tool rt-copy" value=""
                        onChange={e => {
                          const p = recentPlans.find(x => String(x.id) === e.target.value);
                          if (p && window.confirm('Copy that practice into this one? It replaces the current blocks & groups.')) copyFrom(p.id, prettyDate(p.practice_date) + "'s practice");
                          e.target.value = '';
                        }}>
                        <option value="">📋 Copy from…</option>
                        {recentPlans.map(p => (
                          <option key={p.id} value={p.id}>
                            {prettyDate(p.practice_date)}{p.theme ? ` · ${p.theme}` : ''} ({p.slot_count} blocks)
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  {showSeed && (
                    <div className="rt-seed">
                      <label>Warm-up <input type="number" min="0" max="60" value={seedCfg.warmup}
                        onChange={e => setSeedCfg({ ...seedCfg, warmup: e.target.value })} /> min</label>
                      <label>Buffer <input type="number" min="0" max="15" value={seedCfg.buffer}
                        onChange={e => setSeedCfg({ ...seedCfg, buffer: e.target.value })} /> min</label>
                      <label>Rotations
                        <select value={seedCfg.rotations} onChange={e => setSeedCfg({ ...seedCfg, rotations: e.target.value })}>
                          <option value="auto">Auto</option>
                          {[2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </label>
                      <button className="rt-tool primary" onClick={buildRotations}>🪄 Build time slots</button>
                      <span className="ppm-hint">Lays out warm-up + rotations with buffers, timed to the schedule window.</span>
                    </div>
                  )}

                  {showQuick && (
                    <div className="rt-seed">
                      <label>Groups <input type="number" min="1" max="8" placeholder="auto"
                        value={quickCfg.count} onChange={e => setQuickCfg({ ...quickCfg, count: e.target.value })} /></label>
                      <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                        <input type="checkbox" checked={quickCfg.by_level}
                          onChange={e => setQuickCfg({ ...quickCfg, by_level: e.target.checked })} /> group by level
                      </label>
                      <button className="rt-tool primary" onClick={quickBuild} disabled={quickBusy}
                        style={{ background: '#7c3aed', color: '#fff', borderColor: '#7c3aed' }}>
                        {quickBusy ? '⏳ Building…' : '⚡ Build full practice'}
                      </button>
                      <span className="ppm-hint">Splits the class roster into athlete groups, lays out timed rotations, and fills the board. Edit anything after.</span>
                    </div>
                  )}

                  {armedGroup && (
                    <div className="rt-placing">
                      <span>Placing <strong>{(plan.groups || []).find(g => g.id === armedGroup)?.group_label}</strong> — tap cells to drop them in.</span>
                      <button onClick={() => setArmedGroup(null)}>Done</button>
                    </div>
                  )}

                  {/* The rotation table */}
                  {(plan.slots || []).length === 0 ? (
                    <p className="ppm-empty-note">Add a time block to start building rotations.</p>
                  ) : (
                    <div className="rt-table-wrap">
                      <table className="rt-table">
                        <thead>
                          <tr>
                            <th className="rt-time-col">Time block</th>
                            {STATIONS.map(st => <th key={st.key}>{st.emoji} {st.label}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {plan.slots.map((slot, i) => {
                            const whole = !!slot.activity_label;
                            return (
                              <tr key={slot.id}>
                                <td className="rt-time-cell">
                                  <div className="rt-block-label">{slot.label || `R${i + 1}`}</div>
                                  <div className="rt-block-time">
                                    <input type="time" defaultValue={(slot.start_time || '').slice(0, 5)} onBlur={e => updateSlot(slot.id, { start_time: e.target.value || null })} />
                                    <span>–</span>
                                    <input type="time" defaultValue={(slot.end_time || '').slice(0, 5)} onBlur={e => updateSlot(slot.id, { end_time: e.target.value || null })} />
                                  </div>
                                  <label className="rt-whole-toggle">
                                    <input type="checkbox" checked={whole}
                                      onChange={e => updateSlot(slot.id, { activity_label: e.target.checked ? 'Stretch' : null })} /> Whole group
                                  </label>
                                  <select className="rt-block-coach" value={slot.coach_id || ''}
                                    onChange={e => updateSlot(slot.id, { coach_id: e.target.value || null })}>
                                    <option value="">👤 Coach…</option>
                                    {coaches.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                  </select>
                                  <button className="rt-block-del" onClick={() => deleteSlot(slot.id)}>remove</button>
                                </td>

                                {whole ? (
                                  <td className="rt-whole-cell" colSpan={STATIONS.length}>
                                    <span className="rt-whole-lead">All groups:</span>
                                    {WHOLE_PRESETS.map(p => (
                                      <button key={p} className={`rt-wchip ${slot.activity_label === p ? 'on' : ''}`}
                                        onClick={() => updateSlot(slot.id, { activity_label: p })}>{p}</button>
                                    ))}
                                    <input className="rt-wcustom" defaultValue={slot.activity_label || ''}
                                      onBlur={e => updateSlot(slot.id, { activity_label: e.target.value.trim() || 'Stretch' })} placeholder="custom…" />
                                  </td>
                                ) : STATIONS.map(st => {
                                  const placements = placementsInCell(slot.id, st.key);
                                  const held = placements.length === 0 ? heldByOther(slot, st.key) : null;
                                  const kids = cellKids(slot.id, st.key);
                                  const over = kids > STATION_CAP;
                                  return (
                                    <td key={st.key}
                                      className={`rt-cell ${placements.length ? 'filled' : ''} ${held ? 'held' : ''} ${armedGroup ? 'droppable' : ''} ${over ? 'over' : ''}`}
                                      onClick={() => tapCell(slot.id, st.key)}>
                                      {placements.map(({ gp, group: g }) => (
                                        <div key={g.id} className="rt-cell-group" style={{ background: `${groupColor(g.id)}14`, borderColor: groupColor(g.id) }}
                                          onClick={e => e.stopPropagation()}>
                                          <div className="rt-cell-grouptop">
                                            <span className="rt-cell-groupname" style={{ color: groupColor(g.id) }}>
                                              {g.group_label}{g.headcount ? ` (${g.headcount})` : ''}
                                            </span>
                                            <button className="rt-cell-remove" title="Remove group from this station"
                                              onClick={e => { e.stopPropagation(); removePlacement(slot.id, g.id); }}>✕</button>
                                          </div>
                                          <select className="rt-cell-coach" value={gp.coach_id || ''}
                                            onChange={e => setPlacementMeta(slot.id, g.id, { coach_id: e.target.value || null })}>
                                            <option value="">👤 Coach…</option>
                                            {coaches.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                          </select>
                                          <input className="rt-cell-note" defaultValue={gp.note || ''} placeholder="＋ note…"
                                            onBlur={e => { const v = e.target.value.trim(); if (v !== (gp.note || '')) setPlacementMeta(slot.id, g.id, { note: v }); }} />
                                        </div>
                                      ))}
                                      {over && <div className="rt-over">⚠️ {kids} kids</div>}
                                      {held && <div className="rt-held">🔒 {held}</div>}
                                      {!placements.length && !held && <span className="rt-open">{armedGroup ? '＋ tap' : 'open'}</span>}
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="rt-actions">
                    <button className="rot-add" onClick={addSlot}>＋ Add time block</button>
                    <span className="rt-legend"><span className="rt-legend-held" /> = held by another class this block</span>
                  </div>
                </div>
              )}

              {/* SESSION PLAN — theme, goals, drills, workout, S&C (+ bulk apply) */}
              {tab === 'session' && (
                <div className="ppm-session">
                  <div className="ppm-session-head">
                    <div>
                      <h3 className="ppm-section-title">Session Plan</h3>
                      <p className="ppm-hint">The focus, goals, drills and workout for this practice.</p>
                    </div>
                    {plan.class_id && (
                      <button className="ppm-btn ppm-btn-primary" onClick={() => setShowApply(v => !v)}>📅 Apply to more days</button>
                    )}
                  </div>

                  {showApply && plan.class_id && (
                    <div className="ppm-apply">
                      <strong>Roll this session out in bulk</strong>
                      <div className="ppm-apply-row">
                        <span>Repeat on</span>
                        {WEEKDAYS.map((w, i) => (
                          <button key={i} className={`ppm-wd ${applyForm.weekdays.includes(i) ? 'on' : ''}`}
                            onClick={() => toggleApplyWeekday(i)}>{w.slice(0, 3)}</button>
                        ))}
                      </div>
                      <div className="ppm-apply-row">
                        <span>for</span>
                        <input className="ppm-input" type="number" min="1" max="52" style={{ width: 60 }}
                          value={applyForm.weeks} onChange={e => setApplyForm({ ...applyForm, weeks: parseInt(e.target.value) || 1 })} />
                        <span>weeks from {new Date(`${practiceDate}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                      </div>
                      <div className="ppm-apply-row">
                        <span>Copy</span>
                        {[['theme', 'Theme'], ['goals', 'Goals'], ['drills', 'Drills'], ['workout', 'Workout'], ['conditioning', 'S&C']].map(([k, label]) => (
                          <label key={k} className="ppm-inc"><input type="checkbox" checked={applyForm.include[k]} onChange={() => toggleInclude(k)} /> {label}</label>
                        ))}
                      </div>
                      <div style={{ marginTop: 8 }}>
                        <button className="ppm-btn ppm-btn-primary" onClick={runApply}>Apply session</button>
                        <button className="ppm-btn" style={{ marginLeft: 8 }} onClick={() => setShowApply(false)}>Cancel</button>
                      </div>
                      <p className="ppm-hint" style={{ marginTop: 6 }}>Creates or updates the plan for each matching class day — you can still tweak any day afterward.</p>
                    </div>
                  )}

                  {/* Theme & goals */}
                  <div className="ppm-card">
                    <label className="ppm-card-label">🎯 Theme of the day</label>
                    <input className="ppm-input" style={{ width: '100%' }} value={layers.theme}
                      onChange={e => setLayers({ ...layers, theme: e.target.value })}
                      placeholder="e.g. Linear Run · Blocking · Shaping" />
                    <label className="ppm-card-label" style={{ marginTop: 12 }}>✅ Goals for this practice</label>
                    <textarea className="ppm-input" rows={4} style={{ width: '100%' }} value={layers.goals}
                      onChange={e => setLayers({ ...layers, goals: e.target.value })}
                      placeholder={'One per line — e.g.\n• Sprint mechanics: tall posture, fast arms\n• Punch timing off the board'} />
                  </div>

                  {/* Drills — grouped, sectioned lists (heading → Name · Count · Unit) */}
                  <div className="ppm-card">
                    <div className="ppm-card-head">
                      <label className="ppm-card-label">🏋️ Drills, skills &amp; passes</label>
                      <span className="ppm-hint">{(plan.activities || []).length} item{(plan.activities || []).length === 1 ? '' : 's'}</span>
                    </div>

                    {orderedSections().length === 0 && (
                      <p style={{ color: '#888', margin: '4px 0 12px' }}>
                        No sections yet. Add one below — e.g. “Bars”, “Drills for Bars”, or “Floor Passes”.
                      </p>
                    )}

                    {orderedSections().map(sec => {
                      const rows = sectionActivities(sec);
                      const draft = rowDrafts[sec] || { name: '', count: '', unit: 'turns' };
                      return (
                        <div key={sec} className="sec-block">
                          <div className="sec-head">
                            <input className="sec-title" defaultValue={sec === UNGROUPED ? '' : sec}
                              placeholder={sec === UNGROUPED ? 'Name this section…' : 'Section heading'}
                              onBlur={e => renameSection(sec, e.target.value)} />
                            <button className="sec-del" title="Delete section" onClick={() => deleteSection(sec)}>🗑</button>
                          </div>
                          <table className="sec-table">
                            <thead>
                              <tr><th>Name</th><th className="num">Count</th><th>Unit</th><th></th></tr>
                            </thead>
                            <tbody>
                              {rows.map(a => (
                                <tr key={a.id}>
                                  <td>
                                    <input className="sec-name" defaultValue={a.activity_name} list="ppm-drill-list"
                                      onBlur={e => { const v = e.target.value.trim(); if (v && v !== a.activity_name) updateRow(a.id, { activity_name: v }); }} />
                                  </td>
                                  <td className="num">
                                    <input className="sec-count" type="number" min="0" defaultValue={a.reps ?? ''} placeholder="—"
                                      onBlur={e => updateRow(a.id, { reps: e.target.value === '' ? null : parseInt(e.target.value) })} />
                                  </td>
                                  <td>
                                    <select className="sec-unit" value={a.count_unit || 'turns'}
                                      onChange={e => updateRow(a.id, { count_unit: e.target.value })}>
                                      {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                                    </select>
                                  </td>
                                  <td><button className="sec-x" onClick={() => deleteActivity(a.id)}>✕</button></td>
                                </tr>
                              ))}
                              <tr className="sec-addrow">
                                <td>
                                  <input className="sec-name" placeholder="＋ add skill / drill…" value={draft.name} list="ppm-drill-list"
                                    onChange={e => setDraft(sec, { name: e.target.value })}
                                    onKeyDown={e => { if (e.key === 'Enter') addRow(sec); }} />
                                </td>
                                <td className="num">
                                  <input className="sec-count" type="number" min="0" placeholder="#" value={draft.count}
                                    onChange={e => setDraft(sec, { count: e.target.value })}
                                    onKeyDown={e => { if (e.key === 'Enter') addRow(sec); }} />
                                </td>
                                <td>
                                  <select className="sec-unit" value={draft.unit || 'turns'} onChange={e => setDraft(sec, { unit: e.target.value })}>
                                    {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                                  </select>
                                </td>
                                <td><button className="sec-add" title="Add row" onClick={() => addRow(sec)}>＋</button></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      );
                    })}

                    <div className="sec-addsection">
                      <input placeholder="New section — e.g. Bars, Drills for Bars, Floor Passes…" value={newSectionName}
                        onChange={e => setNewSectionName(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') addSectionHeading(); }} />
                      <button className="ppm-btn ppm-btn-primary" onClick={addSectionHeading}>＋ Add section</button>
                    </div>

                    {/* Library suggestions for the name inputs */}
                    <datalist id="ppm-drill-list">
                      {drills.map(d => <option key={d.id} value={d.drill_name} />)}
                    </datalist>
                  </div>

                  {/* Workout */}
                  <div className="ppm-card">
                    <label className="ppm-card-label">📋 Workout / assignments</label>
                    <textarea className="ppm-input" rows={5} style={{ width: '100%' }} value={layers.workout_plan}
                      onChange={e => setLayers({ ...layers, workout_plan: e.target.value })}
                      placeholder="Skills, complexes, numbers, event assignments…" />
                  </div>

                  {/* S&C */}
                  <div className="ppm-card">
                    <label className="ppm-card-label">💪 Strength &amp; conditioning</label>
                    <textarea className="ppm-input" rows={5} style={{ width: '100%' }} value={layers.conditioning_plan}
                      onChange={e => setLayers({ ...layers, conditioning_plan: e.target.value })}
                      placeholder="Conditioning circuits, sets/reps, flexibility…" />
                  </div>

                  <div className="ppm-session-save">
                    <button className="ppm-btn ppm-btn-primary" onClick={saveLayers}>💾 Save session</button>
                    <span className="ppm-hint">Drills save as you add them — this saves theme, goals, workout &amp; S&C.</span>
                  </div>
                </div>
              )}

              {/* ASSIGN — coaches derived from the board; one-tap notify */}
              {tab === 'assign' && (
                <div className="ppm-assign">
                  <h3 className="ppm-section-title">📤 Coaches on this practice</h3>
                  <p className="ppm-hint">Whoever you put on a block or a cell in the Rotation Board shows up here. One tap sends each their part — no re-picking.</p>

                  {boardCoaches.length === 0 ? (
                    <p className="ppm-hint" style={{ padding: '10px 0' }}>
                      No coaches on the board yet. On the <strong>Rotation Board</strong>, pick a coach on a time block or a cell — they’ll appear here.
                    </p>
                  ) : (
                    <>
                      <div className="asg-list">
                        {boardCoaches.map(c => {
                          const scope = c.all
                            ? 'Whole practice'
                            : [
                              c.apparatus.size ? [...c.apparatus].map(k => STATIONS.find(s => s.key === k)?.label || k).join(', ') : null,
                              c.groups.size ? `${c.groups.size} group${c.groups.size === 1 ? '' : 's'}` : null,
                            ].filter(Boolean).join(' · ') || 'On the plan';
                          return (
                            <div key={c.id} className="asg-item">
                              <div className="asg-item-main">
                                <strong>{c.name || 'Coach'}</strong>
                                <span className="asg-scope">{scope}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <button className="ppm-btn ppm-btn-primary" style={{ marginTop: 10 }} onClick={notifyCoaches}>
                        📤 Notify these {boardCoaches.length} coach{boardCoaches.length === 1 ? '' : 'es'}
                      </button>
                      {(plan.assignments || []).length > 0 && (
                        <p className="ppm-hint" style={{ marginTop: 6 }}>✓ {(plan.assignments || []).length} already notified — they see their slice on Today.</p>
                      )}
                    </>
                  )}

                  {/* Fallback: assign a coach who isn't on the board */}
                  <button className="ppm-btn" style={{ marginTop: 16, background: 'none', border: 'none', color: '#4f46e5', cursor: 'pointer', padding: 0 }}
                    onClick={() => setShowManualAssign(v => !v)}>
                    {showManualAssign ? '▲ Hide manual assign' : '＋ Assign a coach not on the board'}
                  </button>
                  {showManualAssign && (
                    <div className="asg-form" style={{ marginTop: 10 }}>
                      <label className="asg-field">
                        <span className="asg-label">Coach</span>
                        <select value={assignForm.coach_id} onChange={e => setAssignForm({ ...assignForm, coach_id: e.target.value })}>
                          <option value="">Select coach…</option>
                          {coaches.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      </label>
                      <div className="asg-field">
                        <span className="asg-label">Events {assignForm.apparatus.length === 0 && <em>· all events</em>}</span>
                        <div className="asg-chips">
                          {STATIONS.map(st => (
                            <button key={st.key} type="button" className={`asg-chip ${assignForm.apparatus.includes(st.key) ? 'on' : ''}`}
                              onClick={() => toggleAssignApp(st.key)}>{st.emoji} {st.label}</button>
                          ))}
                        </div>
                      </div>
                      <div className="asg-field">
                        <span className="asg-label">Groups {assignForm.groups.length === 0 && <em>· all groups</em>}</span>
                        <div className="asg-chips">
                          {(plan.groups || []).length === 0
                            ? <span className="ppm-hint">No groups on this plan yet.</span>
                            : (plan.groups || []).map(g => (
                              <button key={g.id} type="button" className={`asg-chip ${assignForm.groups.includes(g.id) ? 'on' : ''}`}
                                onClick={() => toggleAssignGroup(g.id)}>{g.group_label}</button>
                            ))}
                        </div>
                      </div>
                      <label className="asg-field">
                        <span className="asg-label">Note (optional)</span>
                        <input value={assignForm.note} onChange={e => setAssignForm({ ...assignForm, note: e.target.value })}
                          placeholder="e.g. run bar shaping progressions" />
                      </label>
                      <button className="ppm-btn ppm-btn-primary" onClick={pushAssignment}>📤 Push to coach</button>
                    </div>
                  )}

                  {(plan.assignments || []).length > 0 && (
                    <>
                      <h4 className="ppm-card-label" style={{ marginTop: 18 }}>Notified</h4>
                      <div className="asg-list">
                        {(plan.assignments || []).map(a => {
                          const apps = parseScope(a.apparatus_scope);
                          const grps = parseScope(a.group_scope);
                          const appLabel = apps && apps.length ? apps.map(k => STATIONS.find(s => s.key === k)?.label || k).join(', ') : 'All events';
                          const grpLabel = grps && grps.length ? grps.map(id => (plan.groups || []).find(g => g.id === id)?.group_label || `#${id}`).join(', ') : 'All groups';
                          return (
                            <div key={a.id} className="asg-item">
                              <div className="asg-item-main">
                                <strong>{a.coach_name || 'Coach'}</strong>
                                <span className="asg-scope">{appLabel} · {grpLabel}</span>
                                {a.note && <span className="asg-note">“{a.note}”</span>}
                              </div>
                              <button className="asg-x" title="Remove" onClick={() => removeAssignment(a.id)}>✕</button>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* PREP — data-fed authoring: who to ease up on, who needs work */}
              {tab === 'prep' && (
                <div className="ppm-prep">
                  <h3 className="ppm-section-title">🧭 Prep — who needs what today</h3>
                  <p className="ppm-hint">From today's check-ins + recent analysis for athletes enrolled in this class. Read-only — a glance before you build the plan.</p>
                  {prepLoading ? <p>Loading…</p> : (
                    <div className="ppm-prep-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                      <div>
                        <h4 className="ppm-card-label">⚠️ Ease up today</h4>
                        {(prep?.ease_up || []).length === 0
                          ? <p className="ppm-hint">Nobody flagged — everyone checked in okay (or hasn't checked in yet).</p>
                          : prep.ease_up.map(a => (
                            <div key={a.athlete_id} style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                              <a href={`/athlete-card/${a.athlete_id}?tab=wellness`} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 700, color: '#b91c1c', textDecoration: 'none' }}>{a.name}</a>
                              <div style={{ fontSize: '.8rem', color: '#6b7280' }}>{a.reason}{a.notes ? ` · “${a.notes}”` : ''}</div>
                            </div>
                          ))}
                      </div>
                      <div>
                        <h4 className="ppm-card-label">🎯 Needs work</h4>
                        {(prep?.needs_work || []).length === 0
                          ? <p className="ppm-hint">No open goals or weak-event data yet for this class.</p>
                          : prep.needs_work.map(a => (
                            <div key={a.athlete_id} style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                              <a href={`/athlete-card/${a.athlete_id}?tab=performance`} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 700, color: '#4f46e5', textDecoration: 'none' }}>{a.name}</a>
                              <div style={{ fontSize: '.8rem', color: '#6b7280' }}>
                                {a.weakest_event ? `weak: ${a.weakest_event.event} (${a.weakest_event.rate}% stick)` : ''}
                                {a.weakest_event && a.goals.length ? ' · ' : ''}
                                {a.goals.length ? `goals: ${a.goals.join(', ')}` : ''}
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default PracticePlanModal;
